/**
 * Shared subcomponents for the QC review panel.
 *
 * Kept in a single parts file so the top-level `QcReviewPanel` stays under
 * the eslint max-lines-per-function budget (75). Patterns mirror
 * `pages/case/processing/run-detail-panel/run-detail-panel.parts.tsx`.
 */

import type { TFunction } from 'i18next'
import type React from 'react'

import {
  buildOverallStatusConfig,
  buildResultStatusConfig,
  type StatusStyle,
} from '../../../../components/qc/qc-status-badge/qc-status-badge.helpers'
import type {
  QcOverallStatus,
  QcResultStatus,
} from '../../../../types/qc'

// ---------------------------------------------------------------------------
// Pill / badge
// ---------------------------------------------------------------------------

interface PillProps {
  style: StatusStyle
  compact?: boolean
}

export function Pill({ style, compact }: PillProps): React.ReactElement {
  const size = compact
    ? 'text-[10px] px-2 py-0.5'
    : 'text-xs px-2.5 py-1'
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium ${size} ${style.bg} ${style.text}`}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${style.dot}`} />
      {style.label}
    </span>
  )
}

interface ResultPillProps {
  status: QcResultStatus
  t: TFunction<'qc'>
}

export function ResultPill({ status, t }: ResultPillProps): React.ReactElement {
  const config = buildResultStatusConfig(t)
  return <Pill style={config[status]} compact />
}

interface OverallPillProps {
  status: QcOverallStatus
  t: TFunction<'qc'>
}

export function OverallPill({ status, t }: OverallPillProps): React.ReactElement {
  const config = buildOverallStatusConfig(t)
  return <Pill style={config[status]} />
}

// ---------------------------------------------------------------------------
// Panel header — title + overall verdict + last-run meta + expected-source note
// ---------------------------------------------------------------------------

interface PanelHeaderProps {
  overallStatus: QcOverallStatus | null
  expectedSourceInferred: boolean
  whenRelative: string | null
  unackedCount: number
  t: TFunction<'qc'>
}

export function PanelHeader({
  overallStatus,
  expectedSourceInferred,
  whenRelative,
  unackedCount,
  t,
}: PanelHeaderProps): React.ReactElement {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <h3
          className="text-sm font-semibold text-slate-900 dark:text-slate-100"
          style={{ fontFamily: 'var(--font-display, Syne, sans-serif)' }}
        >
          {t('panel.title')}
        </h3>
        {overallStatus !== null && <OverallPill status={overallStatus} t={t} />}
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">{t('panel.subtitle')}</p>
      {whenRelative && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('panel.lastRun', { when: whenRelative, user: '—' })}
        </p>
      )}
      {expectedSourceInferred && (
        <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-400">
          {t('panel.expectedSourceInferred')}
        </p>
      )}
      {unackedCount > 0 && (
        <p className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400">
          {t('panel.signOffBlocked', { count: unackedCount })}
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Empty / loading / error states (rendered when the panel has no payload yet)
// ---------------------------------------------------------------------------

interface PanelEmptyProps {
  onRun: () => void
  running: boolean
  disabled: boolean
  disabledReason?: string
  t: TFunction<'qc'>
}

export function PanelEmpty({
  onRun,
  running,
  disabled,
  disabledReason,
  t,
}: PanelEmptyProps): React.ReactElement {
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600 dark:text-slate-400">{t('panel.noRunYet')}</p>
      <button
        type="button"
        onClick={onRun}
        disabled={disabled || running}
        title={disabled ? disabledReason : undefined}
        className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400"
      >
        {running ? t('panel.running') : t('panel.runButton')}
      </button>
    </div>
  )
}

interface PanelFooterProps {
  onRerun: () => void
  onExportCsv: () => void
  onExportJson: () => void
  running: boolean
  exporting: boolean
  t: TFunction<'qc'>
}

export function PanelFooter({
  onRerun,
  onExportCsv,
  onExportJson,
  running,
  exporting,
  t,
}: PanelFooterProps): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-2 pt-3">
      <button
        type="button"
        onClick={onRerun}
        disabled={running}
        className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
      >
        {running ? t('panel.running') : t('panel.rerunButton')}
      </button>
      <span className="ml-auto flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        {t('panel.exportLabel')}:
        <button
          type="button"
          onClick={onExportCsv}
          disabled={exporting}
          className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-700"
        >
          {exporting ? t('panel.exporting') : t('panel.exportCsv')}
        </button>
        <button
          type="button"
          onClick={onExportJson}
          disabled={exporting}
          className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:hover:bg-slate-700"
        >
          {exporting ? t('panel.exporting') : t('panel.exportJson')}
        </button>
      </span>
    </div>
  )
}
